import React, { ReactElement, useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  IconButton,
  Modal,
  TextField,
  makeStyles,
} from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import CloseIcon from "@material-ui/icons/Close";
import { useHistory, useLocation } from "react-router-dom";

import theme from "../../../themes";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import {
  BannerContentTypeEnum,
  BannerInput,
  useGetAffinityProgramPartnerQuery,
  useUpdateAffinityProgramPartnerMutation,
  GetAffinityProgramPartnerDocument,
  GetAllAffinityProgramPartnersForChamberDocument,
} from "../../../graphql/types";
import Loader from "../../common/Loader/Loader";
import { FormDescription, FormLabel } from "../../common/ChamberEngageActivationForm";
import LogoPicker from "../../common/utilities/LogoPicker";
import SuccessDialog from "../../common/SuccessDialog";
import ExportButton from "../../common/ExportButton";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import BannerUploadModal from "./BannerUploadModal";
import DeleteBannerModal from "./DeleteBannerModal";
import UploadRoundIcon from "../../../images/upload-rounded.png";
import PlayVideoIcon from "../../../images/play-video-icon.svg";
import EditOrangeIcon from "../../../images/edit-icon-orange.png";
import TrashIcon from "../../../images/trash.png";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { getAffinityProgramRedirectUrlError } from "./affinityProgramRedirectUrl";
import { readAffinityProgramLogoFile } from "./affinityProgramLogo";

const DESCRIPTION_MAX = 250;
const TITLE_MAX = 50;
const BANNER_SLOTS = 4;

type BannerData = {
  image?: string | null;
  videoUrl?: string | null;
  redirectUrl?: string | null;
};

const emptyBanner: BannerData = {};

const useStyles = makeStyles(() => ({
  pageRoot: {
    background: theme.palette.primary.main,
    minHeight: "100vh",
    overflowX: "hidden",
  },
  headerSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: "20px 40px",
    gap: "6px",
    background: theme.palette.primary.main,
    [theme.breakpoints.down("sm")]: {
      padding: "20px 16px",
    },
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    width: "100%",
  },
  title: {
    margin: 0,
    fontSize: "32px",
    color: "#FFFFFF",
    fontWeight: 700,
    fontFamily: theme.typography.fontFamily,
    lineHeight: 1.2,
    [theme.breakpoints.down("sm")]: {
      fontSize: "22px",
    },
  },
  description: {
    fontSize: "18px",
    color: "rgba(255, 255, 255, 0.9)",
    marginTop: "2px",
    lineHeight: "150%",
    maxWidth: "920px",
    fontFamily: theme.typography.fontFamily,
    fontWeight: 500,
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
      lineHeight: "1.55",
    },
  },
  buttonContainer: {
    display: "flex",
    gap: "15px",
    flexWrap: "wrap",
    marginTop: "10px",
  },
  contentContainer: {
    padding: "0 40px 20px 40px",
    background: theme.palette.primary.main,
    [theme.breakpoints.down("sm")]: {
      padding: "0 12px 16px",
    },
  },
  contentCard: {
    background: "#FFFFFF",
    borderRadius: "20px",
    padding: "20px 41px 30px",
    width: "100%",
    minHeight: "806px",
    boxSizing: "border-box",
    display: "flex",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      padding: "16px 14px 24px",
      borderRadius: "16px",
      minHeight: "unset",
    },
  },
  formFrame: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    padding: "40px 51px 49px",
    width: "900px",
    maxWidth: "100%",
    background: "#FFFFFF",
    border: "0.7px solid #888888",
    borderRadius: "31px",
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      padding: "20px 14px 24px",
      borderRadius: "16px",
    },
  },
  form: {
    width: "798px",
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "26px",
    margin: "0 auto",
    [theme.breakpoints.down("sm")]: {
      gap: "20px",
    },
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  characterCount: {
    fontFamily: "Inter, sans-serif",
    color: "#454B58",
    fontSize: "14px",
    lineHeight: "24px",
    marginTop: "4px",
  },
  sectionInput: {
    paddingBottom: "10px",
    borderRadius: "8px",
    width: "100%",
    backgroundColor: "#FFFFFF",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: theme.palette.text.primary,
        borderRadius: "8px",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-error fieldset": {
        borderColor: theme.palette.error.main,
      },
    },
    "& label.Mui-focused": { color: "#000000" },
    "& label.Mui": { color: "black" },
    "& .MuiInputBase-input": {
      fontSize: "16px",
      color: "#333333",
    },
  },
  descriptionInput: {
    paddingBottom: "0px",
    borderRadius: "8px",
    width: "100%",
    backgroundColor: "#FFFFFF",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      alignItems: "flex-start",
      "& fieldset": {
        borderColor: theme.palette.text.primary,
        borderWidth: "1px",
        borderRadius: "8px",
      },
      "&:hover fieldset": { borderColor: theme.palette.text.primary },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: "2px",
      },
      "&.Mui-error fieldset": {
        borderColor: theme.palette.error.main,
      },
    },
    "& .MuiInputBase-inputMultiline": {
      overflow: "auto !important",
    },
    "& .MuiInputBase-input": {
      fontSize: "16px",
      color: "#333333",
    },
  },
  bannerSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "14px",
    width: "100%",
  },
  bannerBadge: {
    backgroundColor: "#00B621",
    color: "#FFFFFF",
    fontFamily: theme.typography.fontFamily,
    fontWeight: 700,
    borderRadius: "30px",
    padding: "14px 38px",
    fontSize: "20px",
    lineHeight: "24px",
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      fontSize: "15px",
      padding: "10px 24px",
      lineHeight: "20px",
    },
  },
  bannerHelp: {
    fontFamily: theme.typography.fontFamily,
    color: "#000000",
    fontSize: "16px",
    lineHeight: "150%",
    margin: 0,
    width: "100%",
    textAlign: "left",
  },
  bannerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: "23px",
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: "16px",
    },
  },
  bannerSlotWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "14px",
    minWidth: 0,
  },
  bannerSlotLabel: {
    fontFamily: theme.typography.fontFamily,
    fontWeight: 600,
    fontSize: "20px",
    lineHeight: "24px",
    color: theme.palette.primary.main,
    textAlign: "center",
    width: "100%",
  },
  bannerSlot: {
    boxSizing: "border-box",
    width: "100%",
    height: "108px",
    backgroundColor: "#FFFFFF",
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: "10px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "9px",
    padding: "22px 12px",
    cursor: "pointer",
    position: "relative",
    overflow: "hidden",
    transition: "background-color 120ms ease, border-color 120ms ease",
    "&:hover": {
      backgroundColor: "#FFF3D6",
    },
  },
  bannerCardFilled: {
    boxSizing: "border-box",
    width: "100%",
    backgroundColor: "#FFFFFF",
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: "10px",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    cursor: "pointer",
  },
  bannerImageContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    position: "relative",
    padding: "8px",
    height: "108px",
    boxSizing: "border-box",
  },
  bannerImageEl: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "4px",
  },
  bannerVideoPlaceholder: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: "4px",
    color: theme.palette.primary.main,
    gap: "4px",
  },
  bannerActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 8px",
    backgroundColor: "transparent",
    gap: "6px",
    [theme.breakpoints.down("sm")]: {
      padding: "4px 4px",
      gap: "4px",
    },
  },
  bannerActionGroup: {
    display: "flex",
    gap: "6px",
    [theme.breakpoints.down("sm")]: { gap: "4px" },
  },
  bannerEditBtn: {
    backgroundColor: "#E3E3E3",
    borderRadius: "5px",
    width: "32px",
    height: "30px",
    minWidth: "32px",
    padding: "4px",
    "&:hover": { backgroundColor: "#D0D0D0" },
    [theme.breakpoints.down("sm")]: {
      width: "24px",
      height: "24px",
      minWidth: "24px",
      padding: "2px",
    },
  },
  bannerTrashBtn: {
    backgroundColor: (theme.palette as any).delete?.main || "#E53935",
    borderRadius: "5px",
    width: "32px",
    height: "30px",
    minWidth: "32px",
    padding: "4px",
    "&:hover": { backgroundColor: "#D41920" },
    [theme.breakpoints.down("sm")]: {
      width: "24px",
      height: "24px",
      minWidth: "24px",
      padding: "2px",
    },
  },
  bannerArrowBtn: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    borderRadius: "5px",
    minWidth: "26px",
    width: "26px",
    height: "24px",
    padding: "2px",
    "&:hover": { backgroundColor: "#D4A353" },
    "&.Mui-disabled": { backgroundColor: "#D9D9D9", color: "#666" },
    [theme.breakpoints.down("sm")]: {
      width: "22px",
      height: "22px",
      minWidth: "22px",
    },
  },
  bannerUploadImg: {
    width: "36px",
    height: "36px",
  },
  bannerUploadText: {
    fontFamily: theme.typography.fontFamily,
    fontWeight: 600,
    fontSize: "20px",
    lineHeight: "24px",
    color: theme.palette.primary.main,
    textAlign: "center",
    margin: 0,
  },
  previewBannersBtn: {
    alignSelf: "center",
    backgroundColor: "#24272C",
    color: "#FFFFFF",
    fontFamily: theme.typography.fontFamily,
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: "150%",
    textTransform: "uppercase",
    borderRadius: "12px",
    padding: "15px 96px",
    minWidth: "264px",
    height: "50px",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#000000" },
    "&.Mui-disabled": {
      backgroundColor: "#5A5F66",
      color: "#FFFFFF",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      padding: "10px 32px",
      minWidth: "200px",
      height: "44px",
    },
  },
  previewModal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  previewModalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: "20px",
    padding: "32px",
    maxWidth: "920px",
    width: "92%",
    maxHeight: "82vh",
    overflowY: "auto",
    outline: "none",
    position: "relative",
  },
  previewModalTitle: {
    fontFamily: theme.typography.fontFamily,
    fontSize: "22px",
    fontWeight: 700,
    color: "#24272C",
    margin: "0 0 20px 0",
    paddingRight: "32px",
  },
  previewModalClose: {
    position: "absolute",
    top: "12px",
    right: "12px",
  },
  previewModalGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "20px",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  previewModalCard: {
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: "10px",
    overflow: "hidden",
    aspectRatio: "16 / 9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FAFAFA",
  },
  previewModalImg: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
  },
  redirectHelp: {
    fontFamily: "Inter, sans-serif",
    color: "#333333",
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 500,
    margin: 0,
  },
  updateButton: {
    alignSelf: "center",
    backgroundColor: "#00B621",
    color: "#FFFFFF",
    fontFamily: theme.typography.fontFamily,
    fontWeight: 700,
    borderRadius: "12px",
    padding: "15px 96px",
    textTransform: "uppercase",
    fontSize: "20px",
    lineHeight: "150%",
    minWidth: "264px",
    height: "50px",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#009A1B" },
    "&.Mui-disabled": {
      backgroundColor: "#86E296",
      color: "#FFFFFF",
    },
  },
}));

type UpdateAffinityProgramLocationState = {
  programId?: string;
  programName?: string;
};

const UpdateAffinityProgram = (): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const history = useHistory();
  const location = useLocation<UpdateAffinityProgramLocationState | undefined>();
  const strings = enumStrings.updateAffinityProgram;

  const programId = location.state?.programId;
  const initialName = location.state?.programName || "";

  const {
    data: partnerData,
    loading: partnerLoading,
  } = useGetAffinityProgramPartnerQuery({
    variables: { id: programId || "" },
    skip: !programId,
    fetchPolicy: "cache-and-network",
  });

  const [updateAffinityProgramPartner] =
    useUpdateAffinityProgramPartnerMutation();

  const [companyName, setCompanyName] = useState<string>(initialName);
  const [logo, setLogo] = useState<string | null>(null);
  const [descriptionText, setDescriptionText] = useState<string>("");
  const [titleText, setTitleText] = useState<string>("");
  const [redirectLink, setRedirectLink] = useState<string>("");
  const [bannersData, setBannersData] = useState<BannerData[]>(
    Array.from({ length: BANNER_SLOTS }, () => ({ ...emptyBanner }))
  );
  const [bannerModalOpen, setBannerModalOpen] = useState(false);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [deleteBannerModalOpen, setDeleteBannerModalOpen] = useState(false);
  const [pendingDeleteBannerIndex, setPendingDeleteBannerIndex] = useState<
    number | null
  >(null);
  const [previewBannersOpen, setPreviewBannersOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFieldErrors, setShowFieldErrors] = useState(false);

  const REQUIRED_FIELD_MESSAGE = strings.requiredFieldError;
  const redirectLinkFieldError =
    getAffinityProgramRedirectUrlError(redirectLink);

  useEffect(() => {
    const partner = partnerData?.getAffinityProgramPartner;
    if (!partner) return;
    setCompanyName(partner.companyName);
    setTitleText(partner.title);
    setDescriptionText(partner.description);
    setRedirectLink(partner.redirectLink);
    setLogo(partner.logo);
    const seededBanners: BannerData[] = Array.from(
      { length: BANNER_SLOTS },
      () => ({ ...emptyBanner })
    );
    (partner.banner || []).forEach((b) => {
      const slot = Math.round(b.position) - 1;
      if (slot < 0 || slot >= BANNER_SLOTS) return;
      seededBanners[slot] = {
        image:
          b.contentType === BannerContentTypeEnum.Image
            ? b.contentUrl
            : undefined,
        videoUrl:
          b.contentType === BannerContentTypeEnum.Video
            ? b.contentUrl
            : undefined,
        redirectUrl: b.imageLink ?? null,
      };
    });
    setBannersData(seededBanners);
  }, [partnerData]);

  useEffect(() => {
    if (initialName && !companyName) {
      setCompanyName(initialName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialName]);

  const handleOpenBannerModal = (index: number) => {
    setCurrentBannerIndex(index);
    setBannerModalOpen(true);
  };

  const handleSaveBanner = (data: BannerData) => {
    setBannersData((prev) => {
      const next = [...prev];
      next[currentBannerIndex - 1] = data;
      return next;
    });
    setBannerModalOpen(false);
  };

  const handleRequestDeleteBanner = (index: number) => {
    setPendingDeleteBannerIndex(index);
    setDeleteBannerModalOpen(true);
  };

  const handleConfirmDeleteBanner = () => {
    if (pendingDeleteBannerIndex !== null) {
      setBannersData((prev) => {
        const next = [...prev];
        next[pendingDeleteBannerIndex] = { ...emptyBanner };
        return next;
      });
    }
    setPendingDeleteBannerIndex(null);
    setDeleteBannerModalOpen(false);
  };

  const handleMoveBanner = (index: number, direction: "left" | "right") => {
    const target = direction === "left" ? index - 1 : index + 1;
    if (target < 0 || target >= bannersData.length) return;
    setBannersData((prev) => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const filledBanners = bannersData.filter((b) => b.image || b.videoUrl);
  const hasAnyBanner = filledBanners.length > 0;

  const handleLogoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setLogo(await readAffinityProgramLogoFile(file));
    } catch {
      setErrorMessage(strings.logoUploadFailed);
      setShowError(true);
    }
  };

  const handleUpdate = async () => {
    if (
      !programId ||
      !companyName.trim() ||
      !descriptionText.trim() ||
      !titleText.trim() ||
      redirectLinkFieldError ||
      !logo
    ) {
      setShowFieldErrors(true);
      setErrorMessage(
        redirectLinkFieldError === "invalid"
          ? strings.invalidRedirectLinkError
          : strings.missingFieldsError
      );
      setShowError(true);
      return;
    }
    setIsUpdating(true);
    try {
      const banner: BannerInput[] = bannersData
        .map((b, idx) => ({ b, position: idx + 1 }))
        .filter(({ b }) => b.image || b.videoUrl)
        .map(({ b, position }) => ({
          contentUrl: (b.image || b.videoUrl) as string,
          contentType: b.image
            ? BannerContentTypeEnum.Image
            : BannerContentTypeEnum.Video,
          imageLink: b.redirectUrl ?? null,
          position,
        }));

      await updateAffinityProgramPartner({
        variables: {
          id: programId,
          input: {
            id: programId,
            companyName: companyName.trim(),
            title: titleText.trim(),
            description: descriptionText.trim(),
            redirectLink: redirectLink.trim(),
            logo,
            banner: banner.length > 0 ? banner : undefined,
          },
        },
        refetchQueries: [
          { query: GetAffinityProgramPartnerDocument, variables: { id: programId } },
          { query: GetAllAffinityProgramPartnersForChamberDocument },
        ],
        awaitRefetchQueries: true,
      });
      setShowSuccess(true);
    } catch (e) {
      setErrorMessage(strings.updateFailed);
      setShowError(true);
    } finally {
      setIsUpdating(false);
    }
  };

  if (partnerLoading && !partnerData) {
    return (
      <div className={classes.pageRoot}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={classes.pageRoot}>
      <div className={classes.headerSection}>
        <div className={classes.titleRow}>
          <div>
            <h1 className={classes.title}>{strings.pageTitle}</h1>
            <p className={classes.description}>{strings.pageDescription}</p>
          </div>
        </div>
        <div className={classes.buttonContainer}>
          <VideoTutorialButton
            label={strings.watchTutorials}
            tutorialUrl=""
          />
          <ExportButton
            customOnClick={() => undefined}
            disablePadding
            icon={
              <InfoOutlinedIcon
                style={{
                  color: theme.palette.primary.main,
                  marginRight: "10px",
                  width: "24px",
                  height: "24px",
                }}
              />
            }
            label={strings.viewInstructions}
          />
        </div>
      </div>

      <div className={classes.contentContainer}>
        <div className={classes.contentCard}>
          <div className={classes.formFrame}>
            <div className={classes.form}>
              <div className={classes.fieldGroup}>
                <FormLabel
                  label={strings.companyNameLabel}
                  required
                  showInfoIcon={false}
                />
                <FormDescription>{strings.companyNameSubtitle}</FormDescription>
                <TextField
                  fullWidth
                  variant="outlined"
                  className={classes.sectionInput}
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  InputLabelProps={{ shrink: false }}
                  error={showFieldErrors && !companyName.trim()}
                  helperText={
                    showFieldErrors && !companyName.trim()
                      ? REQUIRED_FIELD_MESSAGE
                      : ""
                  }
                />
              </div>

              <div className={classes.fieldGroup}>
                <FormLabel
                  label={strings.logoLabel}
                  required={false}
                  showInfoIcon={false}
                />
                <FormDescription>{strings.logoSubtitle}</FormDescription>
                <LogoPicker
                  logo={logo || undefined}
                  base64={handleLogoUpload}
                  deleteLogo={(e: { preventDefault: () => void }) => {
                    e.preventDefault();
                    setLogo(null);
                  }}
                />
              </div>

              <div className={classes.fieldGroup}>
                <FormLabel
                  label={strings.titleLabel}
                  required
                  showInfoIcon={false}
                />
                <FormDescription>{strings.titleSubtitle}</FormDescription>
                <TextField
                  fullWidth
                  variant="outlined"
                  className={classes.sectionInput}
                  placeholder={strings.titlePlaceholder}
                  value={titleText}
                  onChange={(e) =>
                    setTitleText(e.target.value.slice(0, TITLE_MAX))
                  }
                  InputLabelProps={{ shrink: false }}
                  error={showFieldErrors && !titleText.trim()}
                  helperText={
                    showFieldErrors && !titleText.trim()
                      ? REQUIRED_FIELD_MESSAGE
                      : ""
                  }
                />
                <span className={classes.characterCount}>
                  {strings.characterCount
                    .replace("{{current}}", String(titleText.length))
                    .replace("{{max}}", String(TITLE_MAX))}
                </span>
              </div>

              <div className={classes.fieldGroup}>
                <FormLabel
                  label={strings.descriptionLabel}
                  required
                  showInfoIcon={false}
                />
                <FormDescription>
                  {strings.descriptionSubtitle}
                </FormDescription>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  variant="outlined"
                  className={classes.descriptionInput}
                  placeholder={strings.descriptionPlaceholder}
                  value={descriptionText}
                  onChange={(e) =>
                    setDescriptionText(e.target.value.slice(0, DESCRIPTION_MAX))
                  }
                  InputLabelProps={{ shrink: false }}
                  error={showFieldErrors && !descriptionText.trim()}
                  helperText={
                    showFieldErrors && !descriptionText.trim()
                      ? REQUIRED_FIELD_MESSAGE
                      : ""
                  }
                />
                <span className={classes.characterCount}>
                  {strings.characterCount
                    .replace("{{current}}", String(descriptionText.length))
                    .replace("{{max}}", String(DESCRIPTION_MAX))}
                </span>
              </div>

              <div className={classes.bannerSection}>
                <span className={classes.bannerBadge}>
                  {strings.bannersBadge}
                </span>
                <p className={classes.bannerHelp}>{strings.bannersHelp}</p>
                <div className={classes.bannerGrid}>
                  {bannersData.map((banner, idx) => {
                    const hasContent = !!(banner.image || banner.videoUrl);
                    return (
                      <div
                        key={`affinity-banner-slot-${idx}`}
                        className={classes.bannerSlotWrap}
                      >
                        <span className={classes.bannerSlotLabel}>
                          {strings.bannerLabel} {idx + 1}
                        </span>
                        {hasContent ? (
                          <div
                            className={classes.bannerCardFilled}
                            onClick={() => handleOpenBannerModal(idx + 1)}
                          >
                            <div className={classes.bannerImageContainer}>
                              {banner.image ? (
                                <img
                                  src={banner.image}
                                  alt={`Banner ${idx + 1}`}
                                  className={classes.bannerImageEl}
                                />
                              ) : (
                                <div className={classes.bannerVideoPlaceholder}>
                                  <img
                                    src={PlayVideoIcon}
                                    alt="Play"
                                    style={{ width: "28px", height: "28px" }}
                                  />
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Watch Video
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className={classes.bannerActions}>
                              <div className={classes.bannerActionGroup}>
                                <IconButton
                                  size="small"
                                  className={classes.bannerEditBtn}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenBannerModal(idx + 1);
                                  }}
                                >
                                  <img
                                    src={EditOrangeIcon}
                                    alt="Edit"
                                    style={{ width: "16px", height: "16px" }}
                                  />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  className={classes.bannerTrashBtn}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRequestDeleteBanner(idx);
                                  }}
                                >
                                  <img
                                    src={TrashIcon}
                                    alt="Delete"
                                    style={{
                                      width: "16px",
                                      height: "18px",
                                      filter: "brightness(0) invert(1)",
                                    }}
                                  />
                                </IconButton>
                              </div>
                              <div className={classes.bannerActionGroup}>
                                <IconButton
                                  size="small"
                                  className={classes.bannerArrowBtn}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMoveBanner(idx, "left");
                                  }}
                                  disabled={idx === 0}
                                >
                                  <ArrowBackIosIcon
                                    style={{
                                      fontSize: "12px",
                                      marginLeft: "4px",
                                    }}
                                  />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  className={classes.bannerArrowBtn}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMoveBanner(idx, "right");
                                  }}
                                  disabled={idx === bannersData.length - 1}
                                >
                                  <ArrowForwardIosIcon
                                    style={{ fontSize: "12px" }}
                                  />
                                </IconButton>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div
                            className={classes.bannerSlot}
                            onClick={() => handleOpenBannerModal(idx + 1)}
                          >
                            <img
                              src={UploadRoundIcon}
                              alt="Upload"
                              className={classes.bannerUploadImg}
                            />
                            <span className={classes.bannerUploadText}>
                              {strings.uploadBanner}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <Button
                  variant="contained"
                  className={classes.previewBannersBtn}
                  onClick={() => setPreviewBannersOpen(true)}
                  disabled={!hasAnyBanner}
                >
                  {strings.previewBanners}
                </Button>
                <p className={classes.bannerHelp}>{strings.afterPreviewHelp}</p>
              </div>

              <p className={classes.redirectHelp}>{strings.redirectHelp}</p>

              <div className={classes.fieldGroup}>
                <FormLabel
                  label={strings.redirectLinkLabel}
                  required
                  showInfoIcon={false}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  className={classes.sectionInput}
                  placeholder={strings.redirectLinkPlaceholder}
                  value={redirectLink}
                  onChange={(e) => setRedirectLink(e.target.value)}
                  InputLabelProps={{ shrink: false }}
                  error={
                    showFieldErrors &&
                    !!redirectLinkFieldError
                  }
                  helperText={
                    showFieldErrors &&
                    redirectLinkFieldError === "required"
                      ? strings.requiredFieldError
                      : showFieldErrors && redirectLinkFieldError === "invalid"
                      ? strings.invalidRedirectLinkError
                      : ""
                  }
                />
              </div>

              <Button
                variant="contained"
                className={classes.updateButton}
                onClick={handleUpdate}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <CircularProgress size={22} style={{ color: "#fff" }} />
                ) : (
                  strings.updateButton
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <SuccessDialog
        open={showSuccess}
        onClose={() => {
          setShowSuccess(false);
          history.push(RedirectUri.perkPartnersManageActivations);
        }}
        message={strings.updateSuccess}
        variant="success"
        autoDismissMs={5000}
      />
      <SuccessDialog
        open={showError}
        onClose={() => setShowError(false)}
        message={errorMessage}
        variant="error"
      />

      <Modal
        open={previewBannersOpen}
        onClose={() => setPreviewBannersOpen(false)}
        className={classes.previewModal}
      >
        <div className={classes.previewModalContent}>
          <IconButton
            size="small"
            className={classes.previewModalClose}
            onClick={() => setPreviewBannersOpen(false)}
          >
            <CloseIcon />
          </IconButton>
          <h2 className={classes.previewModalTitle}>{strings.bannersBadge}</h2>
          <div className={classes.previewModalGrid}>
            {filledBanners.map((b, idx) => (
              <div key={idx} className={classes.previewModalCard}>
                {b.image ? (
                  <img
                    src={b.image}
                    alt={`Banner preview ${idx + 1}`}
                    className={classes.previewModalImg}
                  />
                ) : (
                  <div className={classes.bannerVideoPlaceholder}>
                    <img
                      src={PlayVideoIcon}
                      alt="Play"
                      style={{ width: "36px", height: "36px" }}
                    />
                    <span style={{ fontSize: "14px", fontWeight: 600 }}>
                      Watch Video
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Modal>

      <BannerUploadModal
        open={bannerModalOpen}
        onClose={() => setBannerModalOpen(false)}
        bannerIndex={currentBannerIndex}
        initialData={bannersData[currentBannerIndex - 1] || {}}
        onSave={handleSaveBanner}
      />

      <DeleteBannerModal
        open={deleteBannerModalOpen}
        onClose={() => {
          setDeleteBannerModalOpen(false);
          setPendingDeleteBannerIndex(null);
        }}
        onDelete={handleConfirmDeleteBanner}
      />
    </div>
  );
};

export default UpdateAffinityProgram;
